import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { useTranslation } from 'react-i18next';

import { useCurrentSite } from '@hotjar/site';
import { useTrackingCode } from '@hotjar/webapp/hooks/useTrackingCode';
import { useModalTrackingCode } from '@hotjar/webapp/hooks/useModalTrackingCode';
import { Dropdown, Link, Alert, Icon, tokens } from '@hotjar/hotsauce';

import { WidgetContainer } from './Widget';

interface TrackingIssueProps {
  trackIndicatorClick: (elementName: string) => void;
  trackClick: (elementName: string) => void;
  trackView: (elementName: string) => void;
}

const StyledLink = styled(Link)`
  margin: 0 0 ${tokens.spacer.x2} ${tokens.spacer.x7};
`;

export const TrackingIssue = ({
  trackIndicatorClick,
  trackClick,
  trackView,
}: TrackingIssueProps): JSX.Element => {
  const currentSite = useCurrentSite().data;
  const { getState } = useTrackingCode();
  const { show } = useModalTrackingCode();

  const { t } = useTranslation();
  const state = getState(currentSite?.lastData);
  const lastActiveHoursAgo = state.lastData.hoursSince ?? 0;
  const lastActiveDaysAgo = Math.ceil(lastActiveHoursAgo / 24) as number;

  const verifyTrackingCode = () => {
    if (!currentSite) {
      return;
    }

    const source = 'Tracking State Button';
    show(currentSite, 2, source);
    trackClick('no_data_tracking_issue_warning_verify-installation');
  };

  // TODO: i18n
  const timeAgoText =
    lastActiveHoursAgo > 24 && lastActiveDaysAgo < 3 ? '24 hours' : `${lastActiveDaysAgo} days`;
  const isHighlighted = lastActiveHoursAgo >= 168;
  return (
    <Dropdown
      css={css`
        background: ${tokens.color.bg_warning};
      `}
      onShow={() => trackView('no_data_tracking_issue_warning')}
      trigger={
        <WidgetContainer
          iconComponent={
            <Icon
              size="m"
              icon="warning_alt"
              color={isHighlighted ? 'text_warning' : 'text_light'}
            />
          }
          onClick={() => trackIndicatorClick('tracking_issue_traffic_coverage_indicator')}
          text={t('common:sampling-widget.tracking-issue.tracking-issue')}
        />
      }
    >
      <Alert
        kind="warning"
        title={t('common:sampling-widget.tracking-issue.has-not-sent-any-data', {
          siteName: currentSite?.name,
          timeAgoText,
        })}
      >
        {t('common:sampling-widget.tracking-issue.there-might-be-an-issue')}
      </Alert>
      <StyledLink as="button" kind="inline" onClick={verifyTrackingCode}>
        {t('common:sampling-widget.tracking-issue.verify-tracking-code-installation')}
      </StyledLink>
    </Dropdown>
  );
};
